import moment from "moment";
import { useEffect, useState } from "react";
import SvgIcon from "../../../parts/SvgIcon";
import TooltipPortal from "../../../parts/tooltip-portal";
import { formattedTarget } from "../../../utils/actuals";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { buildVideoModalUrl } from "../../../utils/video-modal-url";

// stable identity: a fresh [] on every render would re-run the effect below forever
const NO_VIDEOS = [];

/**
 * A row is one video of one set, so the exercise alone doesn't identify it: the
 * same exercise appears once per set and once per video within a set.
 */
const videoRowKey = (video) =>
  `${video.exerciseId}:${video.setIndex}:${video.videoSet}`;

export const VideoListTable = ({
  locals,
  query,
  compareState,
  setVideosToCompareState,
  videosToShow,
  setVideosToShow,
}) => {
  const videoLocals = locals.videoListMetrics?.exercises || NO_VIDEOS;
  const [formState, setFormState] = useState([""]); //
  const formatedDays = new Set([
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
  ]);
  const userMetrics = locals.activeUser.units === "imperial" ? "lb" : "kg";
  /**
   * add more videos to the state
   *
   * The list is filtered by the api, so this only decides between replacing what
   * is on screen (page 1: a new date range, a new filter) and appending to it
   * (pagination). Note pageToShow is always a string here — the refresh action
   * casts the whole query — so it has to be parsed before comparing.
   */
  useEffect(() => {
    const page = Number.parseInt(query.pageToShow, 10) || 1;

    if (page === 1) {
      // the api's order is total (date, then the exercise's order in the
      // workout), so it is the same on every refetch and is also the order the
      // video modal's prev/next walks — take it as is, don't re-arrange it here
      setVideosToShow(videoLocals);
      return;
    }

    if (query.modal) return;

    setVideosToShow((current) => {
      const incoming = new Map(
        videoLocals.map((video) => [videoRowKey(video), video])
      );

      // rows already on screen take the fresh copy of themselves (a comment was
      // added, a video was reviewed); rows from earlier pages stay as they are
      const refreshed = current.map(
        (item) => incoming.get(videoRowKey(item)) || item
      );

      const listed = new Set(current.map(videoRowKey));
      const newVideos = videoLocals.filter(
        (video) => !listed.has(videoRowKey(video))
      );

      return [...refreshed, ...newVideos];
    });
  }, [videoLocals, query.pageToShow, query.modal, setVideosToShow]);

  /**
   * Reset the compare state
   */
  useEffect(() => {
    if (!compareState) {
      setFormState([""]);
      setVideosToCompareState([]);
    }
  }, [compareState, setVideosToCompareState]);
  /**
   * Choose videos to compare
   * @param {*} value
   */
  const handleSelectionChange = (value) => {
    let data = structuredClone(formState);
    if (formState.includes(value)) {
      if (formState.length > 0) {
        const filetered = data.filter((val) => val !== value);
        data = filetered.length > 0 ? filetered : [""];
      }
    } else if (formState.length < 2) {
      if (data.length === 1 && data[0].length === 0) {
        data = [value];
      } else {
        data.push(value);
      }
    }
    setFormState(data);
    setVideosToCompareState(data);
  };
  return (
    <tbody>
      {videosToShow.map((exercise, index) => {
        const precision = locals.activeUser.units === "metric" ? 2 : 1;
        const load = exercise.outcomes?.outcome?.load
          ? `${_.round(
              locals.fromMetric(
                exercise.outcomes.outcome.load,
                locals.activeUser.units
              ),
              precision
            )} ${userMetrics}`
          : 0;

        const exerciseType = exercise.type ?? exercise.exercise?.type ?? "";

        const isPowerlifting =
          exerciseType === "primary" ||
          exerciseType === "secondary" ||
          exercise.exercise?.type === "primary" ||
          exercise.exercise?.type === "secondary";

        const setTarget =
          formattedTarget(
            exercise.outcomes ? exercise.outcomes.outcome : {},
            isPowerlifting,
            exercise.set?.percentRepMax
              ? Number(exercise.set.percentRepMax).toFixed(2)
              : undefined,
            locals.activeUser.units,
            load,
            locals.activeUser.intensityMeasure,
            locals.activeUser.intensityMeasure !== "rpe",
            true
          ) || undefined;

        const setNumber = exercise.setNumber;
        const setComment = exercise.comments || undefined;

        const athleteId = locals.athlete
          ? locals.athlete._id
          : locals.activeUser._id;

        const exercisePrimary =
          exercise.primary ?? exercise.exercise?.primary ?? "";
        const exerciseSecondary =
          exercise.secondary ?? exercise.exercise?.secondary ?? "";
        const exerciseMovement =
          exercise.movement ?? exercise.exercise?.movement ?? "";
        const modalRedirect = buildVideoModalUrl({
          modalType: "watch-video-list",
          video: {
            videoOriginal: exercise.videoOriginal,
            reviewedByTheCoach: exercise.reviewedByTheCoach,
            uploaderId: exercise.uploaderId || athleteId,
            thread: exercise.thread,
          },
          exerciseToEdit: exercise.exerciseId,
          setIndex: Number.parseInt(exercise.setIndex, 10),
          videoIndex: Number.parseInt(exercise.videoSet, 10),
          utcDate: exercise?.utcDate,
          primary: exercisePrimary,
          secondary: exerciseSecondary,
          type: exerciseType,
          movement: exerciseMovement,
          setNumber,
          setTarget,
          setComment,
        });

        let complexReps = "";
        if (exercise.complexOutcomes?.length) {
          exercise.complexOutcomes.forEach((complexOutcome, index) => {
            const reps = complexOutcome.outcomes?.outcome?.reps;
            if (reps && index > 0 && index < exercise.complexOutcomes.length) {
              complexReps += "+";
            }
            if (reps) {
              complexReps += `${reps}`;
            }
          });
          if (complexReps) {
            complexReps = `(${complexReps})`;
          }
        }

        const lastComment = exercise?.thread?.lastComment;
        const lastCommentUser =
          lastComment?.sender ??
          [locals.athlete, locals.activeUser].find(
            (user) => user?._id === lastComment?.senderId
          );
        const lastCommentUserAvatar = getAvatarUrl(lastCommentUser);

        return (
          <tr key={`${exercise.videoOriginal}-${index}-${exercise._id}`}>
            {compareState && (
              <td className="col-compare">
                <SvgIcon
                  src={
                    formState.includes(exercise.videoOriginal)
                      ? "/img/new-icons/checked-square.svg"
                      : "/img/new-icons/unchecked-square.svg"
                  }
                  alt="checked"
                  onClick={() => handleSelectionChange(exercise.videoOriginal)}
                  noFilterOnLight={formState.includes(exercise.videoOriginal)}
                />
              </td>
            )}

            <td>
              {formatedDays.has(moment(exercise.date, "YYYYMMDD").format("DD"))
                ? moment(exercise.date, "YYYYMMDD").format("MMM D, YYYY")
                : moment(exercise.date, "YYYYMMDD").format("MMM DD, YYYY")}
            </td>
            <td>
              {exercise.displayName.charAt(0).toUpperCase() +
                exercise.displayName.slice(1)}
            </td>
            <td className="protocol">
              {formattedTarget(
                exercise.outcomes ? exercise.outcomes.outcome : {},
                exercise.exercise.type === "primary" ||
                  exercise.exercise.type === "secondary",
                exercise.set.percentRepMax
                  ? Number(exercise.set.percentRepMax).toFixed(2)
                  : undefined,
                locals.activeUser.units,
                load,
                locals.activeUser.intensityMeasure,
                locals.activeUser.intensityMeasure !== "rpe",
                true,
                complexReps
              )}{" "}
              (Set #{exercise.setNumber})
            </td>
            <td className="protocol">
              <div>
                {exercise.comments == null ? (
                  <span></span>
                ) : (
                  <TooltipPortal
                    id={"video-table-comment"}
                    body={exercise.comments}
                    childrenContainerClass={"tooltip-coach"}
                  >
                    {exercise.comments.length > 120
                      ? `${exercise.comments.substring(0, 120)}...`
                      : exercise.comments}
                  </TooltipPortal>
                )}
              </div>
            </td>
            <td>
              {exercise.outcomes ? (
                <div>
                  <TooltipPortal
                    id={"video-table-status"}
                    body={
                      exercise.outcomes.status.charAt(0).toUpperCase() +
                      exercise.outcomes.status.slice(1)
                    }
                    childrenContainerClass={"tooltip-coach"}
                  >
                    <div
                      className={`chip ${
                        exercise.outcomes.status === "completed"
                          ? "success"
                          : exercise.outcomes.status === "incomplete"
                            ? "danger"
                            : "warning"
                      }`}
                    ></div>
                  </TooltipPortal>
                </div>
              ) : (
                <div>{""}</div>
              )}
            </td>
            <td>
              {exercise.reviewedByTheCoach && (
                <div>
                  <div className="chip info check" />
                </div>
              )}
            </td>
            <td>
              <div>
                <a href={modalRedirect} className="play-icon-wrapper">
                  <SvgIcon
                    classNames={`play-icon${
                      exercise?.thread ? " threaded" : ""
                    }`}
                    src="/img/new-icons/play-with-gradient.svg"
                    alt="play"
                    noFilterOnLight={true}
                  />
                  {exercise?.thread && (
                    <img
                      className="avatar"
                      src={lastCommentUserAvatar}
                      alt={`last comment user avatar`}
                      loading="lazy"
                    />
                  )}
                </a>
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
};
